import React from "react"
import type { LinkProps } from "@chakra-ui/react"
import { Link as ChaLink } from "@chakra-ui/react"

import isAbsoluteUrl from "is-absolute-url"
import { useRouter } from "lib/router"
import { useZendeskTracking } from "hooks/useZendeskTracking"
import { useParams } from "next/navigation"

type ICustomProps = LinkProps & {
  onClick?: (...args) => void
  inline?: boolean
  useDefaultLocale?: boolean
}

export const Link = React.forwardRef(
  (
    {
      inline = false,
      children,
      useDefaultLocale = true,
      onClick = undefined,
      ...props
    }: ICustomProps,
    ref: React.Ref<HTMLAnchorElement>
  ): JSX.Element => {
    const params = useParams()
    const store = params?.store ?? "en"

    const { href: rawHref = "" } = props
    let href = rawHref

    const router = useRouter()

    href = useZendeskTracking(href)

    if (router && useDefaultLocale) {
      if (!isAbsoluteUrl(rawHref) && store !== "en") {
        href = rawHref.replace(/^\/?/, `/${store}/`)

        // Protect against /us/us/{slug}
        href = href.replace(/(\/us(?=\/))+/i, "/us")
      }
    }

    const content = inline ? <>{children}</> : children
    const dataTestId = props["data-testid"] || "Link"

    const onClickTracking = (...args) => {
      if (onClick) onClick(...args)
    }

    return (
      // Undefined here defaults to Next's default behaviour - which is to
      // make the link relative to the current locale, false takes the link
      // As absolute, allowing us to switch locales

      <ChaLink
        {...props}
        ref={ref}
        data-testid={dataTestId}
        boxShadow="initial"
        href={href}
        onClick={onClickTracking}
        rel="noopener noreferrer"
        role="link"
      >
        {content}
      </ChaLink>
    )
  }
)
